import type {
  RteControlButtonProps as IControlButtonProps,
  RteControlSpec,
} from '@shared/rich-text-editor-core';
import type { ChainedCommands } from '@tiptap/core';
import type { Editor } from '@tiptap/react';

/**
 * Execute a TipTap chained command based on a control operation config.
 *
 * Normalizes invocation:
 * - No attributes -> call with no args.
 * - Array attributes -> spread as args.
 * - Single value -> pass as single arg.
 */
export function executeOperation<TOperation extends keyof ChainedCommands>(
  editor: Editor,
  operation: RteControlSpec<TOperation>['operation'],
) {
  const chain = editor.chain().focus() as unknown as Record<
    string,
    (...args: unknown[]) => ChainedCommands
  >;

  const fn = chain[operation.name] as (...args: unknown[]) => ChainedCommands;

  if (!fn) return;

  const attrs = operation.attributes as unknown;

  if (attrs === undefined) {
    return fn.call(chain).run();
  }

  if (Array.isArray(attrs)) {
    return fn.apply(chain, attrs as unknown[]).run();
  }

  return fn.call(chain, attrs).run();
}

export function ControlButton({
  active,
  disabled,
  onClick,
  children,
  ...rest
}: IControlButtonProps) {
  return (
    <button
      type='button'
      {...rest} // allow aria-*, data-*, title, etc.
      disabled={disabled}
      onClick={(e) => {
        if (!disabled) onClick?.(e);
      }}
      aria-pressed={active ?? undefined} // accessibility for toggle-like controls
      data-active={active || undefined}
      data-disabled={disabled || undefined}
      className={[
        'rte-btn',
        'inline-flex h-8 w-8 items-center justify-center bg-white text-neutral-700',
        'text-base leading-none transition-colors outline-none',
        'hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-50',
        'data-[active=true]:bg-primary-100 data-[active=true]:text-primary',
        'focus-visible:ring-primary focus-visible:ring-2 focus-visible:ring-offset-1',
        rest.className || '',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

export type { IControlButtonProps as ControlButtonProps };

export default ControlButton;
